export interface Donation {
  id: string;
  amount: number;
  donorName: string;
  donorEmail: string;
  message?: string;
  isRecurring: boolean;
  timestamp: string;
  status: 'completed' | 'pending' | 'failed';
}

/**
 * Generate a unique donation ID
 */
const generateDonationId = (): string => {
  return `donation_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Save a new donation record
 */
export const saveDonation = (donationData: Omit<Donation, 'id' | 'status'>): Donation => {
  const donation: Donation = {
    ...donationData,
    id: generateDonationId(),
    status: 'completed'
  };

  // Get existing donations
  const existingDonations = getDonationHistory();
  
  // Add new donation
  const updatedDonations = [donation, ...existingDonations];
  
  // Store updated list
  localStorage.setItem('manivo_donations', JSON.stringify(updatedDonations));
  
  // Store latest donation separately for quick access
  localStorage.setItem('manivo_latest_donation', JSON.stringify(donation));
  
  return donation;
};

/**
 * Get all donation history
 */
export const getDonationHistory = (): Donation[] => {
  try {
    const stored = localStorage.getItem('manivo_donations');
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Failed to parse donation history:', error);
    return [];
  }
};

/**
 * Get the latest donation
 */
export const getLatestDonation = (): Donation | null => {
  try {
    const stored = localStorage.getItem('manivo_latest_donation');
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Failed to parse latest donation:', error);
    return null;
  }
};

/**
 * Get total donation amount
 */
export const getTotalDonated = (): number => {
  const donations = getDonationHistory();
  return donations
    .filter(d => d.status === 'completed')
    .reduce((total, donation) => total + donation.amount, 0);
};

/**
 * Get donation count
 */
export const getDonationCount = (): number => {
  const donations = getDonationHistory();
  return donations.filter(d => d.status === 'completed').length;
};

/**
 * Check if user has made any donations
 */
export const hasDonated = (): boolean => {
  return getDonationCount() > 0;
};

/**
 * Get recurring donations
 */
export const getRecurringDonations = (): Donation[] => {
  const donations = getDonationHistory();
  return donations.filter(d => d.isRecurring && d.status === 'completed');
};

/**
 * Clear all donation data (for testing or reset)
 */
export const clearDonationHistory = (): void => {
  localStorage.removeItem('manivo_donations');
  localStorage.removeItem('manivo_latest_donation');
};

/**
 * Get donations by email (for user identification)
 */
export const getDonationsByEmail = (email: string): Donation[] => {
  const donations = getDonationHistory();
  return donations.filter(d => d.donorEmail.toLowerCase() === email.toLowerCase());
};

/**
 * Get donations for the current month
 */
export const getMonthlyDonations = (): Donation[] => {
  const donations = getDonationHistory();
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  
  return donations.filter(d => {
    const donationDate = new Date(d.timestamp);
    return donationDate.getMonth() === currentMonth && 
           donationDate.getFullYear() === currentYear &&
           d.status === 'completed';
  });
};

/**
 * Format donation amount for display
 */
export const formatDonationAmount = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

/**
 * Get donor level based on total donations
 */
export const getDonorLevel = (totalAmount?: number): { level: string; title: string; color: string } => {
  const total = totalAmount ?? getTotalDonated();
  
  if (total >= 500) {
    return { level: 'platinum', title: 'Platinum Patron', color: 'text-gray-600' };
  } else if (total >= 200) {
    return { level: 'gold', title: 'Gold Supporter', color: 'text-yellow-600' };
  } else if (total >= 100) {
    return { level: 'silver', title: 'Silver Contributor', color: 'text-gray-500' };
  } else if (total >= 25) {
    return { level: 'bronze', title: 'Bronze Friend', color: 'text-amber-600' };
  } else if (total > 0) {
    return { level: 'supporter', title: 'Kind Supporter', color: 'text-green-600' };
  } else {
    return { level: 'none', title: 'New Friend', color: 'text-gray-400' };
  }
}; 